import EventBus from '@core/eventbus';
import { LoginSources, UserEvents } from '@shared-types/idp.types';
import GtmHelper from '@tracking/gtm-helper';
import IdpHandler from '@user/IdpHandler';
import { Trace, Warn } from '@utilities/logging';

declare global {
  interface Window {
    _qual_async: any[];
  }
}
window._qual_async = window._qual_async || [];

const windowMessageCallback = (event: MessageEvent) => {
  try {
    if (event && event.origin.includes('.uefa.com') && typeof event.data !== 'undefined') {
      let message = event.data;
      if (typeof message === 'string') {
        try {
          if (message.indexOf('"gtm"') > -1) {
            message = JSON.parse(message);
            if (message.gtm) {
              GtmHelper._push(message.gtm);
            }
          }
        } catch (err) {
          Warn(err);
        }
      }
    }
  } catch (err) {
    Warn(err);
  }
};

const registerCallback = (eventName: string, callback: (data: any) => void) => {
  window._qual_async.push(['registerChildMessageCallback', eventName, callback]);
};

const pushMessage = (targetId: string, message: string, data: any) => {
  window._qual_async.push(['sendChildMessage', targetId, message, data]);
};

const sendUserInfoToCampaign = async (data: { divId: string }, type: UserEvents) => {
  if (type === UserEvents.LOGIN || type === UserEvents.REQUESTCLOSE) {
    const idp = await IdpHandler.waitIdpReady();
    if (!data || !idp.isLogged()) {
      pushMessage(data.divId, 'requestParticipantAction', true);
      return;
    }
    const usrJwt = await idp.getJwtToken();
    Trace(usrJwt);
    pushMessage(data.divId, 'userInfo', usrJwt);
  }
};

const handleLoginRequest = async (data: { divId: string }) => {
  if (!data) return;

  try {
    const idp = await IdpHandler.waitIdpReady();
    if (idp.isLogged()) {
      sendUserInfoToCampaign(data, UserEvents.LOGIN);
    } else {
      EventBus.on('userLogged', (type: UserEvents) => sendUserInfoToCampaign(data, type));
      EventBus.on(
        'loginModalClosed',
        (event) => {
          Trace('event', event);
          if (event.reason !== 'finished') {
            sendUserInfoToCampaign(data, UserEvents.REQUESTCLOSE);
          }
        },
        this,
      );

      const eventEmitter = idp.getEventEmitter();
      eventEmitter.on(UserEvents.UEFA_DIALOG_CLOSE, (event) => {
        Trace('event', event);
        if (event.reason !== 'finished') {
          sendUserInfoToCampaign(data, UserEvents.REQUESTCLOSE);
        }
      });
      IdpHandler.showLogin(LoginSources.CONTENT);
    }
  } catch (err) {
    Warn(err);
  }
};

const handleError = async (error: { code: string; divId: string }) => {
  Warn(error);
  const idp = await IdpHandler.waitIdpReady();
  switch (error.code) {
    case 'TOKEN_EXPIRED':
    case 'TOKEN_INVALID_FORMAT':
    case 'TOKEN_EMPTY':
    case 'TOKEN_INVALID_SIGNATURE':
    case 'TOKEN_DECRYPTION_FAILED':
      {
        if (idp.isLogged()) {
          const jwt = await idp.getJwtToken();
          pushMessage(error.divId, 'userInfo', jwt);
        } else {
          pushMessage(error.divId, 'requestParticipantAction', true);
        }
      }
      break;
    case 'UNKNOWN':
    default:
      {
        pushMessage(error.divId, 'requestParticipantAction', true);
      }
      break;
  }
};

registerCallback('requestParticipantAction', (data: { divId: string }) => {
  pushMessage(data?.divId, 'requestParticipantAction', false);
});

registerCallback('isLoggedIn', handleLoginRequest);
registerCallback('error', handleError);
window.addEventListener('message', windowMessageCallback);
